import apiClient from "./index";

/**
 * Package interface matching the backend Package model
 */
export interface Package {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  features: string[];
  isActive: boolean;
  serviceId: number;
  stripeProductId?: string;
  stripePriceId?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Fetch all packages
 * @returns Promise<Package[]>
 */
export const getAllPackages = async (): Promise<Package[]> => {
  try {
    const response = await apiClient.get<Package[]>("/packages");
    return response.data;
  } catch (error) {
    console.error("Error fetching all packages:", error);
    throw error;
  }
};

/**
 * Fetch packages by visa type (e.g., "N-400", "I-90")
 * @param visaType - The visa type to fetch packages for
 * @returns Promise<Package[]>
 */
export const getPackagesByServiceId = async (
  visaType: string
): Promise<Package[]> => {
  try {
    const response = await apiClient.get<{ data: Package[]; count: number }>(
      `/packages/service/${visaType}`
    );
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching packages for service ${visaType}:`, error);
    throw error;
  }
};

/**
 * Fetch a single package by ID
 * @param packageId - The ID of the package to fetch
 * @returns Promise<Package>
 */
export const getPackageById = async (packageId: number): Promise<Package> => {
  try {
    const response = await apiClient.get<{ data: Package }>(
      `/packages/${packageId}`
    );
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching package ${packageId}:`, error);
    throw error;
  }
};

/**
 * Create a new package (Admin only)
 * @param packageData - The package data to create
 * @returns Promise<Package>
 */
export const createPackage = async (
  packageData: Omit<Package, "id" | "createdAt" | "updatedAt">
): Promise<Package> => {
  try {
    const response = await apiClient.post<Package>("/packages", packageData);
    return response.data;
  } catch (error) {
    console.error("Error creating package:", error);
    throw error;
  }
};

/**
 * Update an existing package (Admin only)
 * @param packageId - The ID of the package to update
 * @param packageData - The updated package data
 * @returns Promise<Package>
 */
export const updatePackage = async (
  packageId: number,
  packageData: Partial<Package>
): Promise<Package> => {
  try {
    const response = await apiClient.put<Package>(
      `/packages/${packageId}`,
      packageData
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating package ${packageId}:`, error);
    throw error;
  }
};

/**
 * Delete a package (Admin only)
 * @param packageId - The ID of the package to delete
 * @returns Promise<void>
 */
export const deletePackage = async (packageId: number): Promise<void> => {
  try {
    await apiClient.delete(`/packages/${packageId}`);
  } catch (error) {
    console.error(`Error deleting package ${packageId}:`, error);
    throw error;
  }
};
